/**
 * ActivityPage - Category-Based Activity Navigation
 *
 * Provides tab-based navigation for Events, Training Groups, and Volunteer activities.
 * Replaces time-based Events page with category-based organization.
 *
 * 3-Layer CSS Architecture (Nightfall Theme):
 * - Uses --oro-* semantic tokens (Layer 2) ONLY
 * - Never references --nightfall-* base tokens directly
 * - Tailwind utilities map to Layer 3 (@theme aliases)
 *
 * Issue: #728 (Activity Page Redesign)
 * Issue: #742 (Club context filtering)
 * Related: #727 (Training Groups)
 */

import { Routes, Route, Navigate, NavLink, useNavigate } from 'react-router-dom';
import { TopBar } from '../components';
import EventsTab from './tabs/EventsTab';
// GroupsTab hidden for v1.2 release (#1241) - re-enable in v1.2.1
// import GroupsTab from './tabs/GroupsTab';
// VolunteerTab hidden for App Store compliance (#3277) - "coming soon"
// placeholder scrubbed from the shipped binary; re-enable when the volunteer
// feature exists (VolunteerTab.tsx kept in place).
import { useClubNavContext } from '../hooks/useClubNavContext';
import { useClub } from '../hooks/useClubs';

export default function ActivityPage() {
  const navigate = useNavigate();
  // Club context for filtering (#742)
  const clubSlug = useClubNavContext();
  const { data: clubData } = useClub(clubSlug || '');
  const clubId = clubData?.club?.id;

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      {/* Top Bar - Fixed at top, shows club switcher (#742, #917) */}
      {/* Added back button for navigation from other pages (#1095) */}
      <TopBar title={clubData?.club?.name || 'Activity'} showClubSwitcher onBack={() => navigate(-1)} />

      {/* Tab Bar - Uses 3-layer CSS: text-text-secondary → --text-secondary → --nightfall-text-secondary */}
      <div className="fixed top-14 left-0 right-0 flex border-b border-oro-border bg-oro-bg z-10">
        <TabLink to="/activity/events">Events</TabLink>
        {/* Training Groups tab hidden for v1.2 release (#1241) - re-enable in v1.2.1 */}
        {/* <TabLink to="/activity/groups">Training Groups</TabLink> */}
        {/* Volunteer tab hidden for App Store compliance (#3277) - re-enable when the feature ships */}
        {/* <TabLink to="/activity/volunteer">Volunteer</TabLink> */}
      </div>

      {/* Tab Content - Scrollable with top padding for fixed tab bar */}
      <div className="flex-1 overflow-y-auto pt-[7rem]">
        {/* 7rem = 3.5rem (top bar) + 3.5rem (tab bar) */}
        <Routes>
          <Route index element={<Navigate to="/activity/events" replace />} />
          <Route path="events" element={<EventsTab clubId={clubId} clubSlug={clubSlug} />} />
          {/* Training Groups route hidden for v1.2 release (#1241) - re-enable in v1.2.1 */}
          {/* <Route path="groups" element={<GroupsTab clubId={clubId} />} /> */}
          <Route path="groups" element={<Navigate to="/activity/events" replace />} />
          {/* Volunteer route redirects for App Store compliance (#3277) - the
              "coming soon" placeholder is scrubbed; existing links land on Events */}
          <Route path="volunteer" element={<Navigate to="/activity/events" replace />} />
        </Routes>
      </div>
    </div>
  );
}

/**
 * TabLink - Nightfall-compliant tab navigation link
 * 
 * 3-Layer CSS Compliance:
 * - text-text-secondary (inactive) → --text-secondary → --nightfall-text-secondary
 * - text-text-primary (hover) → --text-primary → --nightfall-text-primary
 * - text-accent (active) → --oro-gold → --nightfall-gold
 * - border-accent (active) → --oro-gold → --nightfall-gold
 * - border-oro-border (hover) → --oro-border-hover → --nightfall-border-hover
 */
function TabLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex-1 px-4 py-3 font-medium text-center transition-colors ${
          isActive
            ? 'text-accent border-b-2 border-accent'
            : 'text-text-secondary hover:text-text-primary border-b-2 border-transparent hover:border-oro-border'
        }`
      }
    >
      {children}
    </NavLink>
  );
}
